import { Link, useSearchParams } from 'react-router-dom';
import type { components } from '@bookingdei/api-client/types';
import { formatDate } from '@bookingdei/i18n/format-date';
import { api } from '../api';
import { useLanguage } from './language-context';
import { useResource } from './use-resource';

type CategoryList = components['schemas']['CategoryList'];
type LocationList = components['schemas']['LocationList'];
type LocationNode = components['schemas']['LocationNode'];

// The collapsed search pill in the desktop header (design DC:112-121, logic
// DC:4837-4841; CB-17 website-only `header-search-pill.tsx`). Shown on the search and
// business routes in place of the centre nav, it summarises the current search —
// service · location · when — and takes the visitor back to the home hero to change it.
//
// The summary is read from the URL the search screen already owns (q, category,
// location, available_on), so the pill never holds search state of its own. Labels
// come from the registries (FR-L6), falling back to the design's "All services /
// Current location / Any time" when a part is unset.

function findLocation(nodes: LocationNode[], id: string): LocationNode | undefined {
  for (const n of nodes) {
    if (n.id === id) return n;
    const hit = findLocation(n.children ?? [], id);
    if (hit) return hit;
  }
  return undefined;
}

export function HeaderSearchPill() {
  const { t, lang } = useLanguage();
  const [params] = useSearchParams();
  const q = params.get('q')?.trim() ?? '';
  const categoryId = params.get('category') ?? '';
  const locationId = params.get('location') ?? '';
  const date = params.get('available_on') ?? '';

  const categories = useResource<CategoryList | null>(
    (signal) => (categoryId ? (api.GET('/registries/categories', { signal, lang }) as Promise<CategoryList>) : Promise.resolve(null)),
    [categoryId, lang],
  );
  const locations = useResource<LocationList | null>(
    (signal) => (locationId ? (api.GET('/registries/locations', { signal, lang }) as Promise<LocationList>) : Promise.resolve(null)),
    [locationId, lang],
  );

  const cat = categories.data?.items.find((c) => c.id === categoryId);
  const loc = locationId ? findLocation(locations.data?.items ?? [], locationId) : undefined;

  const service = cat ? ((lang === 'bn' ? cat.label_bn : cat.label_en) ?? cat.label_en) : q || t('v3home.search_all_services');
  const where = loc ? ((lang === 'bn' ? loc.name_bn : loc.name_en) ?? loc.name_en) : t('v3home.search_current_location');
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00`) : null;
  const when = parsed && !Number.isNaN(parsed.getTime()) ? formatDate(lang, parsed) : t('v3home.search_any_time');

  return (
    <Link className="hpill" to="/" aria-label={t('pill.edit_search', { service, where, when })}>
      <span className="hpill__svc">{service}</span>
      <span className="hpill__sep" aria-hidden="true">
        ·
      </span>
      <span className="hpill__part">{where}</span>
      <span className="hpill__sep" aria-hidden="true">
        ·
      </span>
      <span className="hpill__part">{when}</span>
      <span className="hpill__go" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <circle cx="11" cy="11" r="6.5" />
          <path d="m20 20-4.2-4.2" />
        </svg>
      </span>
    </Link>
  );
}
